import { useEffect, useRef, useState } from 'react';
import { useParams, useSearchParams, Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth0Safe as useAuth0 } from '@/hooks/useAuth0Safe';
import { Helmet } from 'react-helmet-async';
import usePageMeta from '@/hooks/usePageMeta';
import { toast } from 'sonner';
import ArticleCard from '@/components/ArticleCard';
import AdSlot from '@/components/AdSlot';
import ArticleProgressBar from '@/components/article/ArticleProgressBar';
import ArticleShareBar from '@/components/article/ArticleShareBar';
import ArticleTTS from '@/components/article/ArticleTTS';
import ArticleTOC from '@/components/article/ArticleTOC';
import ArticleDonation from '@/components/article/ArticleDonation';
import ArticleComments from '@/components/article/ArticleComments';
import ArticleReactions from '@/components/article/ArticleReactions';
import ArticleBody from '@/components/article/ArticleBody';
import {
  Clock, User, Eye, Tag, Lock, Download, Languages, BookOpen, ExternalLink,
} from 'lucide-react';
import Lightbox from 'yet-another-react-lightbox';
import 'yet-another-react-lightbox/styles.css';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND = process.env.REACT_APP_BACKEND_URL;

const LOCALES = [
  { code: 'hr', label: 'HR' },
  { code: 'en', label: 'EN' },
  { code: 'de', label: 'DE' },
  { code: 'es', label: 'ES' },
  { code: 'fr', label: 'FR' },
  { code: 'it', label: 'IT' },
  { code: 'nl', label: 'NL' },
  { code: 'pt', label: 'PT' },
  { code: 'zh', label: '中文' },
  { code: 'ar', label: 'AR' },
  { code: 'hi', label: 'HI' },
  { code: 'ru', label: 'RU' },
  { code: 'ja', label: 'JA' },
  { code: 'sl', label: 'SL' },
  { code: 'sk', label: 'SK' },
  { code: 'hu', label: 'HU' },
];

export default function ArticlePage() {
  const { slug } = useParams();
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { isAuthenticated, getAccessTokenSilently, loginWithRedirect } = useAuth0();

  const urlLang = searchParams.get('lang') || i18n.language;
  const [currentLang, setCurrentLang] = useState(urlLang);
  const [article, setArticle] = useState(null);
  const [related, setRelated] = useState([]);
  const [paywalled, setPaywalled] = useState(false);
  const [loading, setLoading] = useState(true);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const bodyRef = useRef(null);

  useEffect(() => { loadArticle(currentLang); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [slug, currentLang, isAuthenticated]);

  // Sync article language with GLOBAL language switcher (top nav). Without
  // this effect, switching the language in the header did nothing on the
  // article page — the language was only read once on mount.
  useEffect(() => {
    // Only follow the global switcher when the URL has no explicit ?lang= override
    const urlOverride = searchParams.get('lang');
    if (urlOverride) return;
    if (i18n.language && i18n.language !== currentLang) {
      setCurrentLang(i18n.language);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [i18n.language]);

  async function loadArticle(lang) {
    setLoading(true);
    try {
      const headers = {};
      if (isAuthenticated) {
        try { const token = await getAccessTokenSilently(); headers.Authorization = `Bearer ${token}`; } catch {}
      }
      // Encode slug — articles with diacritics or non-ASCII slugs would 404
      // when passed raw. Always pin lang so backend can localise.
      const slugEnc = encodeURIComponent(slug);
      let r = await fetch(`${API}/public/articles/${slugEnc}?lang=${lang}`, { headers });
      // 404 fallback: retry without lang param ONLY when lang is non-HR.
      // Backend always finds the article by slug; localisation happens
      // afterwards. This protects against any edge case where the lang
      // query string trips routing/cache without masking real HR 404s.
      if (!r.ok && r.status === 404 && lang && lang !== 'hr') {
        r = await fetch(`${API}/public/articles/${slugEnc}`, { headers });
      }
      if (!r.ok) throw new Error('Not found');
      const d = await r.json();
      setArticle(d.article);
      setRelated(d.related || []);
      setPaywalled(d.paywalled || false);
    } catch { setArticle(null); }
    finally { setLoading(false); }
  }

  function changeLang(l) {
    setCurrentLang(l);
    const sp = new URLSearchParams(searchParams);
    sp.set('lang', l);
    setSearchParams(sp, { replace: true });
  }

  // Build SEO/OG meta once article is loaded and push them into <head>
  // imperatively — react-helmet-async 1.3.0 drops meta children when the
  // Helmet block grows large, but title still works, so we keep Helmet for
  // the title only and use usePageMeta for the rest.
  const _coverUrl = article?.cover_image_url
    ? (article.cover_image_url.startsWith('http') ? article.cover_image_url : `${BACKEND}${article.cover_image_url}`)
    : '';
  // Branded share card takes precedence over the raw cover — it packs the
  // article title, author and category into a 1200x630 image that drives
  // far better CTR on Facebook/Twitter/LinkedIn.
  const _shareCardUrl = article?.share_card_url
    ? (article.share_card_url.startsWith('http') ? article.share_card_url : `${BACKEND}${article.share_card_url}`)
    : '';
  const _canonical = article ? `https://e-mina.eu/clanak/${article.slug || slug}` : '';
  const _shareDescription = article ? (article.excerpt_hr || article.meta_description || '').slice(0, 300) : '';
  const _shareImage = _shareCardUrl || _coverUrl || 'https://e-mina.eu/og-default.jpg';

  usePageMeta(article ? {
    title: `${article.title_hr} — e-mina.eu`,
    description: _shareDescription,
    canonical: _canonical,
    og: {
      type: 'article',
      site_name: 'e-mina.eu',
      title: article.title_hr,
      description: _shareDescription,
      url: _canonical,
      image: _shareImage,
      'image:width': '1200',
      'image:height': '630',
      'image:alt': article.title_hr,
      locale: 'hr_HR',
    },
    article: {
      published_time: article.publish_at || '',
      author: article.author?.name || 'e-mina.eu',
      section: article.category?.name_hr || '',
    },
    twitter: {
      card: 'summary_large_image',
      site: '@eminaeu',
      title: article.title_hr,
      description: _shareDescription,
      image: _shareImage,
      'image:alt': article.title_hr,
    },
    jsonLd: {
      '@context': 'https://schema.org',
      '@type': 'NewsArticle',
      headline: article.title_hr,
      description: _shareDescription,
      image: [_shareImage],
      datePublished: article.publish_at,
      author: { '@type': 'Person', name: article.author?.name || 'e-mina.eu' },
      publisher: { '@type': 'Organization', name: 'e-mina.eu', logo: { '@type': 'ImageObject', url: 'https://e-mina.eu/logo.png' } },
      mainEntityOfPage: _canonical,
    },
  } : {});

  if (loading) return <div className="flex items-center justify-center py-20 text-gray-400">{t('common.loading')}</div>;
  if (!article) return <div className="text-center py-20 text-gray-400">404</div>;

  const coverUrl = article.cover_image_url?.startsWith('http') ? article.cover_image_url : `${BACKEND}${article.cover_image_url}`;
  const date = article.publish_at ? new Date(article.publish_at).toLocaleDateString(currentLang === 'hr' ? 'hr-HR' : currentLang, { day: 'numeric', month: 'long', year: 'numeric' }) : '';
  const gallery = (article.gallery_image_urls || []).map(url => url.startsWith('http') ? url : `${BACKEND}${url}`);
  const readMin = article.estimated_read_minutes || 1;

  const pageUrl = typeof window !== 'undefined' ? `${window.location.origin}/clanak/${slug}` : '';

  // Resolve display fields: backend already returns translation via
  // resolve_lang when ?lang= is passed, but we keep a safety net so any
  // future missing translation still shows HR rather than empty text.
  const displayTitle = article.title_hr || '';
  const displayExcerpt = article.excerpt_hr || '';

  // Detect missing translation so we can surface a banner (UX fix for
  // "switching language does nothing" bug — actually backend silently
  // falls back to HR when the requested locale has no stored translation).
  const hasTranslation = currentLang === 'hr' || !!(
    (article.title_translations && article.title_translations[currentLang]) ||
    (article.body_translations && article.body_translations[currentLang])
  );

  async function downloadPdf() {
    const toastId = toast.loading('Generiram PDF…');
    try {
      const r = await fetch(`${API}/public/articles/${slug}/export.pdf?lang=${currentLang}`);
      if (!r.ok) throw new Error();
      const blob = await r.blob();
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `${slug}-${currentLang}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      toast.success('PDF preuzet', { id: toastId });
    } catch { toast.error('Greška pri generiranju PDF-a', { id: toastId }); }
  }

  return (
    <>
      <Helmet>
        <title>{`${article.title_hr} — e-mina.eu`}</title>
      </Helmet>

      <ArticleProgressBar targetRef={bodyRef} />

      <div className="max-w-4xl mx-auto px-4 py-8" data-testid="article-page" data-article-id={article.id}>
        {article.category && (
          <Link to={`/kategorija/${article.category.slug}`} className="inline-block text-[11px] font-bold uppercase tracking-wider text-white px-2 py-0.5 rounded mb-3" style={{ backgroundColor: article.category.color || '#3b82f6' }} data-testid="article-category">
            {article.category.name_hr}
          </Link>
        )}
        {article.is_premium && <span className="inline-flex items-center gap-1 ml-2 text-[11px] font-bold uppercase tracking-wider text-amber-600 bg-amber-50 px-2 py-0.5 rounded"><Lock size={10} /> PREMIUM</span>}

        <h1 className="text-2xl md:text-4xl font-bold text-gray-900 dark:text-gray-100 leading-tight mb-4" data-testid="article-title">{article.title_hr}</h1>

        <div className="flex flex-wrap items-center gap-4 text-sm text-gray-500 dark:text-gray-400 mb-4">
          <span className="flex items-center gap-1.5" data-testid="article-author">
            <User size={14} />
            {article.author?.slug ? (
              <Link to={`/impressum#${article.author.slug}`} className="hover:text-red-600 transition-colors">
                {article.author?.name || 'e-mina.eu'}
              </Link>
            ) : (
              <span>{article.author?.name || 'e-mina.eu'}</span>
            )}
            {article.author?.position && (
              <span className="text-[11px] text-gray-400">— {article.author.position}</span>
            )}
          </span>
          {date && <span className="flex items-center gap-1.5"><Clock size={14} /> {date}</span>}
          <span className="flex items-center gap-1.5"><Eye size={14} /> {article.views} {t('article.views')}</span>
          <span className="flex items-center gap-1.5" data-testid="article-read-min"><BookOpen size={14} /> {readMin} {t('article.read_minutes')}</span>
        </div>

        {/* Actions row: TTS + PDF + Lang */}
        <div className="flex flex-wrap items-center gap-2 mb-5 pb-5 border-b border-gray-100 dark:border-gray-800">
          <ArticleTTS title={article.title_hr} body={article.body_hr} lang={currentLang} />
          <button onClick={downloadPdf} className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-200" data-testid="article-pdf-btn">
            <Download size={13} /> {t('article.download_pdf')}
          </button>
          <div className="inline-flex items-center gap-1.5 bg-gray-100 dark:bg-gray-800 rounded-lg px-2 py-1">
            <Languages size={13} className="text-gray-500" />
            <select value={currentLang} onChange={e => changeLang(e.target.value)} className="bg-transparent text-xs font-medium outline-none text-gray-700 dark:text-gray-200" data-testid="article-lang-select">
              {LOCALES.map(l => <option key={l.code} value={l.code} className="text-gray-900 bg-white dark:bg-gray-800 dark:text-gray-100">{l.label}</option>)}
            </select>
          </div>
        </div>

        <ArticleShareBar
          title={article.title_hr}
          url={pageUrl}
          articleId={article.id}
          excerpt={displayExcerpt || article.excerpt_hr}
          shareImageUrl={_shareCardUrl || _coverUrl}
          storyImageUrl={article.share_card_story_url
            ? (article.share_card_story_url.startsWith('http')
                ? article.share_card_story_url
                : `${BACKEND}${article.share_card_story_url}`)
            : ''}
        />

        {!hasTranslation && (
          <div className="mb-4 px-4 py-2.5 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 text-amber-800 dark:text-amber-300 text-xs rounded-lg flex items-center gap-2" data-testid="translation-missing-banner">
            <Languages size={14} />
            <span>Prijevod za ovaj članak nije dostupan na odabranom jeziku. Prikazan je hrvatski izvornik.</span>
          </div>
        )}

        <ArticleDonation article={article} />

        {article.cover_image_url && (
          <div className="aspect-[16/9] rounded-xl overflow-hidden mb-8 bg-gray-100 dark:bg-[#111827]">
            <img src={coverUrl} alt={article.title_hr} className="w-full h-full object-cover" data-testid="article-cover" />
          </div>
        )}

        <ArticleTOC bodyRef={bodyRef} />

        {/* Body */}
        <div ref={bodyRef} className="relative">
          <ArticleBody html={article.body_hr} paywalled={paywalled} />
          {paywalled && (
            <div className="absolute bottom-0 left-0 right-0" data-testid="paywall-overlay">
              <div className="h-32 bg-gradient-to-t from-white dark:from-[#0b1220] to-transparent" />
              <div className="bg-white dark:bg-[#111827] border-t border-gray-200 dark:border-gray-800 rounded-xl p-8 text-center shadow-lg">
                <Lock size={32} className="text-amber-500 mx-auto mb-3" />
                <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100 mb-2">{t('article.paywall_heading')}</h3>
                <p className="text-gray-500 text-sm mb-5">{t('article.paywall_body')}</p>
                <div className="flex gap-3 justify-center">
                  <Link to="/pretplata" className="px-6 py-2.5 bg-red-600 text-white rounded-lg font-medium text-sm hover:bg-red-700" data-testid="paywall-subscribe-btn">{t('article.paywall_subscribe')}</Link>
                  {!isAuthenticated && <button onClick={() => loginWithRedirect()} className="px-6 py-2.5 border border-gray-300 text-gray-700 rounded-lg font-medium text-sm hover:bg-gray-50" data-testid="paywall-login-btn">{t('article.paywall_login')}</button>}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Source of content */}
        {!paywalled && (article.source_name || article.source_url || article.photo_credit) && (
          <div className="mt-6 text-xs text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-[#111827] border border-gray-200 dark:border-gray-800 rounded-lg px-4 py-3 space-y-1.5" data-testid="article-source">
            {(article.source_name || article.source_url) && (
              <div className="flex items-start gap-2">
                <ExternalLink size={12} className="mt-0.5 shrink-0" />
                <span>
                  {t('article.source_label')}{' '}
                  {article.source_url ? (
                    <a
                      href={article.source_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="font-semibold text-red-600 hover:underline"
                      data-testid="article-source-link"
                    >
                      {article.source_name || article.source_url}
                    </a>
                  ) : (
                    <strong className="text-gray-700 dark:text-gray-200">{article.source_name}</strong>
                  )}
                </span>
              </div>
            )}
            {article.photo_credit && (
              <div className="flex items-start gap-2" data-testid="article-photo-credit">
                <span className="mt-0.5 shrink-0 text-[10px]">📷</span>
                <span>
                  {t('article.photography_label', 'Fotografija:')}{' '}
                  <strong className="text-gray-700 dark:text-gray-200">{article.photo_credit}</strong>
                </span>
              </div>
            )}
          </div>
        )}

        {/* Gallery */}
        {gallery.length > 0 && !paywalled && (() => {
          const cols = Math.min(Math.max(Number(article.gallery_columns) || 3, 1), 6);
          return (
          <section className="mt-8 pt-6 border-t border-gray-200 dark:border-gray-800" data-testid="article-gallery">
            <h3 className="text-sm font-bold uppercase tracking-wider text-gray-900 dark:text-gray-100 border-l-4 border-blue-600 pl-3 mb-4">{t('article.gallery_title')}</h3>
            <div className="grid gap-2" style={{ gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))` }}>
              {gallery.map((url, i) => (
                <button key={i} onClick={() => { setLightboxIndex(i); setLightboxOpen(true); }} className="aspect-square rounded-lg overflow-hidden bg-gray-100 dark:bg-[#111827] hover:opacity-90 transition-opacity" data-testid={`gallery-thumb-${i}`}>
                  <img src={url} alt="" className="w-full h-full object-cover" loading="lazy" />
                </button>
              ))}
            </div>
            <Lightbox open={lightboxOpen} close={() => setLightboxOpen(false)} index={lightboxIndex} slides={gallery.map(url => ({ src: url.replace(/-(\d{2,4})x(\d{2,4})(\.[a-z]+)(\?.*)?$/i, '$3$4'), thumb: url }))} carousel={{ finite: false }} on={{ view: ({ index }) => setLightboxIndex(index) }} render={{ slide: ({ slide }) => (<img src={slide.src} alt="" onError={(e) => { if (slide.thumb && e.currentTarget.src !== slide.thumb) e.currentTarget.src = slide.thumb; }} style={{ maxWidth: '100%', maxHeight: '100vh', objectFit: 'contain', margin: '0 auto', display: 'block' }} />) }} />
          </section>
          );
        })()}

        {/* Tags */}
        {article.tags_detail && article.tags_detail.length > 0 && !paywalled && (
          <div className="mt-8 pt-6 border-t border-gray-200 dark:border-gray-800">
            <div className="flex items-center gap-2 flex-wrap">
              <Tag size={14} className="text-gray-400" />
              {article.tags_detail.map(tag => (
                <Link key={tag.id} to={`/pretraga?q=${tag.name}`} className="px-3 py-1 text-xs bg-gray-100 dark:bg-[#111827] rounded-full text-gray-600 dark:text-gray-300 hover:bg-blue-50 hover:text-blue-600 transition-colors">{tag.name}</Link>
              ))}
            </div>
          </div>
        )}

        {/* Comments */}
        <ArticleReactions slug={slug} />
        <ArticleComments slug={slug} />

        {/* In-article ad */}
        <div className="mt-10 flex justify-center">
          <AdSlot size="580x300" position="article-bottom" />
        </div>

        {/* Related */}
        {related.length > 0 && (
          <section className="mt-12 pt-8 border-t border-gray-200 dark:border-gray-800">
            <h2 className="text-base font-bold uppercase tracking-wider text-gray-900 dark:text-gray-100 border-l-4 border-blue-600 pl-3 mb-6">{t('article.related')}</h2>
            <div className="grid sm:grid-cols-3 gap-6">{related.map(a => <ArticleCard key={a.id} article={a} />)}</div>
          </section>
        )}
      </div>
    </>
  );
}
